import type { EntitiesState } from '@redux/entities'
import type { ProviderPriceListItemModel, ProviderPriceListItemOptionModel } from '@models/types'

import useSelector from '@hooks/useSelector'

type SelectedOption = {
  id: number,
  title: string,
  value: {
    id: number,
    price: number,
    title: string,
  },
}

export type Row = {
  basePrice: number,
  key: string,
  optionsTotal: number,
  provider_price_list_item_id: number,
  provider_price_list_item_option_id: number,
  provider_price_list_item_option_value_id: number,
  quantity: number,
  selectedOptions: SelectedOption[],
  title: string,
}

type BuildOptionsParams = {
  entities: EntitiesState,
  filteredOptions: ProviderPriceListItemOptionModel[],
  index?: number,
  key?: string,
  optionsString?: string,
  optionsTotal?: number,
  providerPriceListItem: ProviderPriceListItemModel,
  selectedOptions?: SelectedOption[],
}

const buildOptions = (params: BuildOptionsParams) => {
  const {
    entities,
    filteredOptions,
    index = 0,
    key = '',
    optionsString = '',
    optionsTotal: currentTotal = 0,
    providerPriceListItem,
    selectedOptions = [],
  } = params

  const { providerPriceListItemOptionValues } = entities

  let rows: Row[] = []

  const currentOption = filteredOptions[index] || {}
  const nextOption = filteredOptions[index + 1]

  const filteredOptionValues = Object.values(providerPriceListItemOptionValues)
    .filter(optionValue => optionValue.provider_price_list_item_option_id === currentOption.id)

  // No Options
  if (index === 0 && filteredOptionValues.length === 0){
    return [{
      basePrice: providerPriceListItem.price,
      key,
      optionsTotal: currentTotal,
      provider_price_list_item_id: currentOption?.provider_price_list_item_id,
      provider_price_list_item_option_id: null,
      provider_price_list_item_option_value_id: null,
      quantity: providerPriceListItem.quantity,
      selectedOptions: [],
      title: '',
    }]
  }

  // Loop through options
  filteredOptionValues.forEach((priceListItemOptionValue) => {
    const { id, price, title } = priceListItemOptionValue

    const mergedOptions: SelectedOption[] = [
      // @ts-expect-error TS2322
      ...selectedOptions,
      {
        id: currentOption.id,
        // @ts-expect-error TS2322
        title: currentOption.title,
        value: {
          id,
          price,
          title,
        },
      },
    ]

    const mergedKey = `${key}.${id}`
    const total = currentTotal + price
    const mergedOptionsString = `${optionsString}/${title}`

    const row = {
      basePrice: providerPriceListItem.price,
      key: mergedKey,
      optionsTotal: total,
      provider_price_list_item_id: currentOption?.provider_price_list_item_id,
      provider_price_list_item_option_id: currentOption?.id,
      provider_price_list_item_option_value_id: id,
      quantity: providerPriceListItem.quantity,
      selectedOptions: mergedOptions,
      title: mergedOptionsString,
    }

    if (nextOption){
      const nestedRows = buildOptions({
        entities,
        filteredOptions,
        index: index + 1,
        key: mergedKey,
        optionsTotal: total,
        providerPriceListItem,
        optionsString: mergedOptionsString,
        selectedOptions: mergedOptions,
      })

      // @ts-expect-error TS2322
      rows = [...rows, ...nestedRows]
    } else {
      rows.push(row)
    }
  })

  return rows
}

type BuildRowsParams = {
  entities: EntitiesState,
  providerPriceListItem: ProviderPriceListItemModel,
}

const buildRows = (params: BuildRowsParams) => {
  const {
    entities,
    entities: {
      providerPriceListItemOptions,
    },
    providerPriceListItem,
  } = params

  const filteredOptions = Object.values(providerPriceListItemOptions)
    .filter(option => option.provider_price_list_item_id === providerPriceListItem.id)

  const rows = buildOptions({
    entities,
    filteredOptions,
    key: `${providerPriceListItem.id}`,
    providerPriceListItem,
  })

  return rows
}

type UsePriceListMatrixItemOptions = {
  providerPriceListItem: ProviderPriceListItemModel,
}

function usePriceListMatrixItem(options: UsePriceListMatrixItemOptions) {
  const {
    providerPriceListItem,
  } = options

  const entities = useSelector(reduxState => reduxState.entities)

  const rows = buildRows({
    entities,
    providerPriceListItem,
  })

  return {
    rows,
  }
}

export default usePriceListMatrixItem
